import { Restaurant, Order } from '@/types';
import { httpClient, PaginatedResponse } from './http';
import { API_ENDPOINTS } from '../config/api';

// Tipos de respuesta del backend

const mapBackendOrder = (backendOrder: any): Order => ({
  id: backendOrder.id,
  restaurantId: backendOrder.restaurantId || backendOrder.restaurant_id,
  restaurantName: backendOrder.restaurantName || backendOrder.restaurant_name,
  items: (backendOrder.items || []).map((item: any) => ({
    id: item.id,
    menuItem: {
      id: item.menuItemId || item.menu_item_id || item.id,
      name: item.menuItemName || item.menu_item_name || item.name || 'Producto',
      description: item.description || '',
      price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
      category: item.category || 'General',
      available: true,
    },
    quantity: item.quantity,
    price: parseFloat(item.price || item.unitPrice || item.unit_price || 0),
    notes: item.notes,
    specialInstructions: item.specialInstructions || item.special_instructions,
    size: item.size, // Agregar el campo size
  })),
  totalAmount: parseFloat(backendOrder.totalAmount || backendOrder.total_amount || 0),
  status: backendOrder.status,
  orderDate: new Date(backendOrder.createdAt || backendOrder.created_at),
  estimatedDelivery: backendOrder.estimatedDelivery ? new Date(backendOrder.estimatedDelivery) : undefined,
  actualDelivery: backendOrder.actualDelivery ? new Date(backendOrder.actualDelivery) : undefined,
  deliveryAddress: backendOrder.deliveryAddress || backendOrder.delivery_address,
  paymentMethod: backendOrder.paymentMethod || backendOrder.payment_method || 'card',
  notes: backendOrder.notes,
  rating: backendOrder.rating,
  review: backendOrder.review,
});

// Servicio de restaurantes
export const restaurantService = {
  // Obtener todos los restaurantes (público)
  getRestaurants: async (): Promise<Restaurant[]> => {
    try {
      console.log('🔄 RestaurantService - Llamando endpoint:', API_ENDPOINTS.PUBLIC_RESTAURANTS);
      const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_RESTAURANTS);
      console.log('📡 RestaurantService - Respuesta del backend:', response);
      
      // El backend devuelve {data: [...], pagination: {...}}
      const restaurantsData = response?.data || response;
      console.log('📊 RestaurantService - Datos procesados:', restaurantsData);
      
      if (Array.isArray(restaurantsData)) {
        console.log('✅ RestaurantService - Retornando', restaurantsData.length, 'restaurantes');
        return restaurantsData;
      } else {
        console.error('❌ RestaurantService - Los datos no son un array:', typeof restaurantsData);
        return [];
      }
    } catch (error) {
      console.error('❌ RestaurantService - Error fetching restaurants:', error);
      return [];
    }
  },

  // Obtener restaurantes por usuario (administrativo)
  getRestaurantsByUserId: async (userId: string): Promise<Restaurant[]> => {
    try {
      const response = await httpClient.get<Restaurant[]>(API_ENDPOINTS.RESTAURANTS(userId), undefined, 'authenticated');
      return response;
    } catch (error) {
      console.error('Error fetching restaurants by user ID:', error);
      return [];
    }
  },

  // Obtener restaurantes a los que el usuario tiene acceso (como owner o manager/kitchen)
  getRestaurantsWithAccess: async (userId: string, userEmail?: string): Promise<Restaurant[]> => {
    try {
      console.log('🏪 getRestaurantsWithAccess - Llamando endpoint con:', { userId, userEmail });
      
      // Si no hay email, hacer fallback al endpoint por userId
      if (!userEmail) {
        console.log('🏪 getRestaurantsWithAccess - Sin email, usando fallback getRestaurantsByUserId');
        return await restaurantService.getRestaurantsByUserId(userId);
      }
      
      const endpoint = `${API_ENDPOINTS.RESTAURANTS(userId)}/access/${encodeURIComponent(userEmail)}`;
      const response = await httpClient.get<any[]>(endpoint, undefined, 'authenticated');
      console.log('🏪 getRestaurantsWithAccess - Respuesta del backend:', response);
      const mappedRestaurants = response;
      console.log('🏪 getRestaurantsWithAccess - Restaurantes mapeados:', mappedRestaurants.length);
      return mappedRestaurants;
    } catch (error) {
      console.error('❌ Error fetching restaurants with access:', error);
      return [];
    }
  },

  // Obtener restaurante por ID (público)
  getRestaurantById: async (restaurantId: string): Promise<Restaurant | null> => {
    try {
      const response = await httpClient.get<Restaurant>(API_ENDPOINTS.PUBLIC_RESTAURANT_BY_ID(restaurantId));
      return response;
    } catch (error) {
      console.error('Error fetching restaurant by ID:', error);
      return null;
    }
  },

  // Obtener restaurantes por categoría
  getRestaurantsByCategory: async (category: string): Promise<Restaurant[]> => {
    try {
      const allRestaurants = await restaurantService.getRestaurants();
      return allRestaurants.filter(restaurant => 
        restaurant.categories.includes(category)
      );
    } catch (error) {
      console.error('Error fetching restaurants by category:', error);
      return [];
    }
  },

  // Obtener restaurantes destacados (público)
  getFeaturedRestaurants: async (): Promise<Restaurant[]> => {
    try {
      const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_FEATURED_RESTAURANTS);
      const restaurantsData = response?.data || response;
      
      if (Array.isArray(restaurantsData)) {
        return restaurantsData;
      } else {
        console.error('❌ RestaurantService - Featured restaurants no es un array:', typeof restaurantsData);
        return [];
      }
    } catch (error) {
      console.error('Error fetching featured restaurants:', error);
      return [];
    }
  },

  // Buscar restaurantes (público)
  searchRestaurants: async (query: string): Promise<Restaurant[]> => {
    try {
      const response = await httpClient.get<any>(API_ENDPOINTS.PUBLIC_SEARCH_RESTAURANTS, { query });
      const restaurantsData = response?.data || response;
      
      if (Array.isArray(restaurantsData)) {
        return restaurantsData;
      } else {
        console.error('❌ RestaurantService - Search restaurants no es un array:', typeof restaurantsData);
        return [];
      }
    } catch (error) {
      console.error('Error searching restaurants:', error);
      return [];
    }
  },

  // Obtener pedidos del cliente
  getClientOrders: async (userId: string): Promise<Order[]> => {
    try {
      // Nota: Necesitamos restaurantId para usar el endpoint correcto
      console.warn('getClientOrders: Necesita restaurantId para funcionar correctamente', userId);
      return [];
    } catch (error) {
      console.error('Error fetching client orders:', error);
      return [];
    }
  },

  // Obtener pedido por ID
  getOrderById: async (userId: string, orderId: string): Promise<Order | null> => {
    try {
      // Nota: Necesitamos restaurantId para usar el endpoint correcto
      console.warn('getOrderById: Necesita restaurantId para funcionar correctamente', userId, orderId);
      return null;
    } catch (error) {
      console.error('Error fetching order by ID:', error);
      return null;
    }
  },

  // Obtener pedidos por restaurante
  getOrdersByRestaurant: async (userId: string, restaurantId: string): Promise<Order[]> => {
    try {
      // Usar el endpoint correcto con restaurantId
      const response = await httpClient.get<PaginatedResponse<any>>(API_ENDPOINTS.ORDERS(userId, restaurantId), undefined, 'authenticated');
      return response.data.map(mapBackendOrder);
    } catch (error) {
      console.error('Error fetching orders by restaurant:', error);
      return [];
    }
  },

  // Crear nuevo pedido
  createOrder: async (userId: string, order: Omit<Order, 'id' | 'orderDate'>): Promise<Order> => {
    try {
      const orderData = {
        restaurant_id: order.restaurantId,
        table_id: '1', // Default table ID
        items: order.items.map(item => ({
          menu_item_id: item.id,
          quantity: item.quantity,
          notes: item.notes,
        })),
        payment_method: order.paymentMethod,
        notes: order.notes || '',
      };
      console.log('createOrder: orderData AUGAAAs', orderData, userId, order);

      const response = await httpClient.post<Order>(API_ENDPOINTS.PUBLIC_ORDERS, orderData, 'public');
      return mapBackendOrder(response);
    } catch (error) {
      console.error('Error creating order:', error);
      throw new Error('Error al crear el pedido');
    }
  },

  // Actualizar estado del pedido
  updateOrderStatus: async (userId: string, orderId: string, status: Order['status']): Promise<Order | null> => {
    try {
      // Nota: Necesitamos restaurantId para usar el endpoint correcto
      console.warn('updateOrderStatus: Necesita restaurantId para funcionar correctamente', userId, orderId, status);
      return null;
    } catch (error) {
      console.error('Error updating order status:', error);
      return null;
    }
  },

  // Calificar pedido
  rateOrder: async (userId: string, orderId: string, rating: number, review?: string): Promise<Order | null> => {
    try {
      // Nota: Necesitamos restaurantId para usar el endpoint correcto
      console.warn('rateOrder: Necesita restaurantId para funcionar correctamente', userId, orderId, rating, review);
      return null;
    } catch (error) {
      console.error('Error rating order:', error);
      return null;
    }
  },

  // Crear nuevo restaurante (administrativo)
  createRestaurant: async (userId: string, restaurantData: {
    name: string;
    description?: string;
    image?: string;
    rating?: number;
    cuisine?: string;
    address?: string;
    phone?: string;
    is_open?: boolean;
    delivery_time?: string;
    minimum_order?: number;
    delivery_fee?: number;
    categories?: string[];
    featured?: boolean;
  }): Promise<Restaurant | null> => {
    try {
      // Validar campo obligatorio
      if (!restaurantData.name) {
        throw new Error('El nombre del restaurante es obligatorio');
      }

      const response = await httpClient.post<Restaurant>(API_ENDPOINTS.RESTAURANTS(userId), restaurantData, 'authenticated');
      return response;
    } catch (error) {
      console.error('Error creating restaurant:', error);
      throw error;
    }
  },

}; 